import {
  BundleItemType as GqlBundleItemType,
  BundlePricing,
} from 'generated/graphql';
import { FormMode } from './otpBundles.model';
import {
  AllContent,
  AllPrograms,
  RawContentItem,
  RawOtpBundle,
  RawPrograms,
} from '../../common/bundle.types';
import { Program, BundleItemType } from '@playbook-technologies/common-enums';
import { GraphQLError } from 'graphql';

export const getBundleFormDataByBundleId = (
  bundleId: number,
  bundles: RawOtpBundle[],
  rawContent: RawContentItem[],
  allPrograms: AllPrograms,
  mode: FormMode,
) => {
  const bundle = bundles.find(
    (b) => b.id === bundleId && b.pricing === BundlePricing.Otp,
  );
  if (!bundle) {
    return;
  }

  const name = mode === 'duplicate' ? `${bundle.title} Copy` : bundle?.title;
  const playbookFee =
    mode === 'edit' ? bundle?.oneTimeProduct?.playbookFee : undefined;

  return {
    name,
    price: bundle?.oneTimeProduct?.price
      ? String(bundle.oneTimeProduct.price / 100)
      : '',
    description: bundle?.description || undefined,
    features: (bundle?.items || [])
      .filter((i) => i.itemType === GqlBundleItemType.Feature)
      .map((i) => i.itemId) as number[],
    programs: toCopyProgramIds(mode, allPrograms, bundle?.items),
    content: toCopyContentIds(mode, rawContent, bundle?.items),
    playbookFee,
    isFree: Boolean(bundle.isFree),
  };
};

const toCopyContentIds = (
  mode: FormMode,
  rawContent: RawContentItem[],
  bundleItems: RawOtpBundle['items'],
): string[] => {
  if (!bundleItems) return [];

  const contentItemsToCopy = bundleItems.filter(
    (i) => i.itemType === GqlBundleItemType.Content,
  );
  if (contentItemsToCopy.length === 0) return [];

  if (mode === 'edit') {
    return contentItemsToCopy.map((i) => i.contentId) as string[];
  }

  return contentItemsToCopy
    .filter((c) => {
      const contentItem = rawContent.find((i) => i.value.id === c.contentId);
      return !contentItem?.value.isArchived;
    })
    .map((i) => i.contentId) as string[];
};

const toCopyProgramIds = (
  mode: FormMode,
  allPrograms: AllPrograms,
  bundleItems: RawOtpBundle['items'],
): number[] => {
  if (!bundleItems) return [];

  const programsToCopy = bundleItems.filter(
    (i) => i.itemType === GqlBundleItemType.Program,
  );
  if (programsToCopy.length === 0) return [];

  if (mode === 'edit') {
    return programsToCopy.map((i) => i.itemId) as number[];
  }

  return programsToCopy
    .filter((p) => {
      const program = allPrograms.find((i) => i.id === p.itemId);
      return !program?.isArchived;
    })
    .map((i) => i.itemId) as number[];
};

export const toAllOtpPrograms = (programs?: RawPrograms) =>
  (programs || [])
    .filter((p) => p.accessType === Program.AccessType.Exclusive.value)
    .map((p) => ({
      id: p.id,
      name: p.name || '',
      isPublished: !!p.publishedAt && !p.archivedAt,
      isArchived: !!p.archivedAt,
      readiness: p.readiness ?? null,
    }));

export const toSellableOtpPrograms = (programs?: RawPrograms) =>
  (programs || [])
    .filter((p) => p.accessType === Program.AccessType.Exclusive.value)
    .filter((p) => !p.archivedAt)
    .map((p) => ({
      id: p.id,
      name: p.name || '',
      isPublished: !!p.publishedAt,
      readiness: p.readiness ?? null,
    }));

type ErrorDetail = {
  itemType: string;
  itemId?: number;
  contentId?: string;
  title?: string;
};
export type MutationErrors = Array<
  GraphQLError & {
    code?: string;
    details?: Array<ErrorDetail>;
  }
>;

export const getMutationErrors = (
  errors: MutationErrors,
  allPrograms: AllPrograms,
  allContent: AllContent,
): string | string[] =>
  errors.map((e) => {
    if (e?.code === 'OTP_BUNDLE_VALIDATION_ERROR') {
      const details = (e?.details || []).map(
        (d) => `• ${getItemTitle(d, allPrograms, allContent)}`,
      );
      return `${e.message}:\n${details.join('\n')}`;
    }
    return e.message;
  });

const notFound = 'not found';

const getItemTitle = (
  details: ErrorDetail,
  allPrograms: AllPrograms,
  allContent: AllContent,
) => {
  if (details.itemType === BundleItemType.PROGRAM) {
    const program = allPrograms.find((p) => p.id === details.itemId);
    return program?.name || notFound;
  }

  if (details.itemType === BundleItemType.CONTENT) {
    const contentItem = allContent.find((c) => c.id === details.contentId);
    return contentItem?.name || notFound;
  }

  return notFound;
};

export const toSavedProgramIds = (
  items: RawOtpBundle['items'] = [],
): number[] =>
  items
    .filter((i) => i.itemType === GqlBundleItemType.Program)
    .map((i) => i.itemId) as number[];
export const toSavedContentIds = (
  items: RawOtpBundle['items'] = [],
): string[] =>
  items
    .filter((i) => i.itemType === GqlBundleItemType.Content)
    .map((i) => i.contentId) as string[];
